import React, { useState, useEffect } from 'react';

// Services
import fileService from '../services/fileService';
import folderService from '../services/folderService';
import settingsService from '../services/settingsService';
import SidebarLayout from '../components/SidebarLayout';

// UI
import { Button } from "../components/ui/button"
import { useToast } from "../components/ui/use-toast"
import FileList from '../components/FileList';

// Icons
import {
  Loader2,
  Home,
  ChevronRight,
  UploadCloud,
  FolderPlus,
  FolderOpen,
  ArrowLeft,
  Sparkles,
} from 'lucide-react';

// Dialogs
import FileUpload from '../components/FileUpload';
import FolderUpload from '../components/FolderUpload';
import CreateFolder from '../components/CreateFolder';
import PdfViewer from '../components/PdfViewer';
import ImageViewer from '../components/ImageViewer';
import FloatingActionMenu from '../components/FloatingActionMenu';

const Dashboard = () => {
  const { toast } = useToast();
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'My Drive' }]);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Dialog States
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [folderUploadDialogOpen, setFolderUploadDialogOpen] = useState(false);
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);

  const [storageData, setStorageData] = useState(null);

  // Viewer States
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [currentPdfFile, setCurrentPdfFile] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImageFile, setCurrentImageFile] = useState(null);

  const [defaultViewMode, setDefaultViewMode] = useState('compact');

  // Drag and Drop State
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files && files.length > 0) {
      setDroppedFiles(files);
      setUploadDialogOpen(true);
    }
  };

  useEffect(() => {
    if (!uploadDialogOpen) setDroppedFiles(null);
  }, [uploadDialogOpen]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [filesData, foldersData, storage, settings] = await Promise.all([
        fileService.getFiles(currentFolder),
        folderService.getFolders(currentFolder),
        fileService.getStorageStats(),
        settingsService.getSettings()
      ]);
      setFiles(filesData.files);
      setFolders(foldersData.folders);
      setStorageData(storage);
      setDefaultViewMode(settings.default_view_mode);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({ variant: "destructive", title: "Error", description: "Gagal memuat data" });
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      const [filesData, foldersData, storage] = await Promise.all([
        fileService.getFiles(currentFolder),
        folderService.getFolders(currentFolder),
        fileService.getStorageStats()
      ]);
      setFiles(filesData.files);
      setFolders(foldersData.folders);
      setStorageData(storage);
    } catch (error) {
      console.error('Error loading files:', error);
      toast({ variant: "destructive", title: "Error", description: "Gagal memuat file" });
    }
  };

  const handleFileDelete = async (fileId) => {
    try {
      await fileService.deleteFile(fileId);
      toast({ title: "Berhasil", description: "File berhasil dihapus" });
      loadFiles();
    } catch { toast({ variant: "destructive", title: "Gagal", description: "Gagal menghapus file" }); }
  };

  const handleFolderDelete = async (folderId) => {
    try {
      await folderService.deleteFolder(folderId);
      toast({ title: "Berhasil", description: "Folder berhasil dihapus" });
      loadFiles();
    } catch { toast({ variant: "destructive", title: "Gagal", description: "Gagal menghapus folder" }); }
  };

  const handleFileRename = async (fileId, newName) => {
    try {
      await fileService.renameFile(fileId, newName);
      toast({ title: "Berhasil", description: "File berhasil diubah namanya" });
      loadFiles();
    } catch { toast({ variant: "destructive", title: "Gagal", description: "Gagal mengubah nama file" }); }
  };

  const handleFolderRename = async (folderId, newName) => {
    try {
      await folderService.renameFolder(folderId, newName);
      toast({ title: "Berhasil", description: "Folder berhasil diubah namanya" });
      loadFiles();
    } catch { toast({ variant: "destructive", title: "Gagal", description: "Gagal mengubah nama folder" }); }
  };

  const handleFileMove = async (fileId, folderId, fileName) => {
    try {
      await fileService.moveFile(fileId, folderId);
      toast({ title: "Berhasil", description: `File ${fileName} berhasil dipindahkan` });
      loadFiles();
    } catch { toast({ variant: "destructive", title: "Gagal", description: "Gagal memindahkan file" }); }
  };

  const handleFileDownload = async (file) => {
    try {
      toast({ title: "Mengunduh...", description: `Mengunduh ${file.original_name}` });
      await fileService.downloadFile(file.id, file.original_name);
      toast({ title: "Berhasil", description: "File berhasil diunduh" });
    } catch { toast({ variant: "destructive", title: "Gagal", description: "Gagal mengunduh file" }); }
  };

  const handleFolderDownload = async (folder) => {
    try {
      toast({ title: "Mengunduh...", description: `Mengunduh folder ${folder.name}` });
      await folderService.downloadFolder(folder.id, folder.name);
      toast({ title: "Berhasil", description: "Folder berhasil diunduh sebagai ZIP" });
    } catch { toast({ variant: "destructive", title: "Gagal", description: "Gagal mengunduh folder" }); }
  };

  const handleFolderOpen = (folder) => {
    setCurrentFolder(folder.id);
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
  };

  const handleFolderClick = (folder) => {
    if (folder) {
      handleFolderOpen(folder);
    } else {
      setCurrentFolder(null);
      setBreadcrumbs([{ id: null, name: 'My Drive' }]);
    }
  };

  const handleBreadcrumbClick = (index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolder(newBreadcrumbs[newBreadcrumbs.length - 1].id);
  };

  const handleBackButton = () => {
    if (breadcrumbs.length > 1) {
      const newBreadcrumbs = breadcrumbs.slice(0, -1);
      setBreadcrumbs(newBreadcrumbs);
      setCurrentFolder(newBreadcrumbs[newBreadcrumbs.length - 1].id);
    }
  };

  const handlePdfView = (file) => {
    setCurrentPdfFile({ id: file.id, name: file.original_name, downloadUrl: `/api/files/download/${file.id}` });
    setPdfViewerOpen(true);
  };

  const handleImageView = (file) => {
    setCurrentImageFile({ id: file.id, name: file.original_name, downloadUrl: `/api/files/download/${file.id}` });
    setImageViewerOpen(true);
  };

  if (loading) {
    return (
      <SidebarLayout storageData={{ storage: null }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <Loader2 style={{ width: '40px', height: '40px', animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
            <span style={{ color: '#94a3b8', fontSize: '14px' }}>Memuat data...</span>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout storageData={storageData}>
      <div
        style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag Overlay */}
        {isDragging && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(59,130,246,0.15)',
            backdropFilter: 'blur(4px)',
            border: '3px dashed #3b82f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <div style={{
              background: '#fff', padding: '40px 56px', borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(59,130,246,0.25)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
              animation: 'bounceIn 0.3s ease',
            }}>
              <UploadCloud style={{ width: '64px', height: '64px', color: '#3b82f6' }} />
              <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#3b82f6', margin: 0 }}>Lepaskan file untuk mengunggah</h2>
              <p style={{ color: '#94a3b8', margin: 0, fontSize: '14px' }}>Mendukung banyak file sekaligus</p>
            </div>
          </div>
        )}


        {/* Main Content */}
        <main style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Top Bar */}
          <div style={{
            padding: '0 32px',
            height: '68px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(226,232,240,0.8)',
            position: 'sticky',
            top: 0,
            zIndex: 20,
            boxShadow: '0 1px 8px rgba(0,0,0,0.04)',
          }}>
            {/* Breadcrumb */}
            <nav>
              <ol style={{ display: 'flex', alignItems: 'center', gap: '4px', listStyle: 'none', margin: 0, padding: 0 }}>
                {breadcrumbs.map((crumb, index) => (
                  <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {index > 0 && <ChevronRight style={{ width: '14px', height: '14px', color: '#cbd5e1' }} />}
                    <button
                      onClick={() => handleBreadcrumbClick(index)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '4px 8px', borderRadius: '8px', border: 'none',
                        cursor: index === breadcrumbs.length - 1 ? 'default' : 'pointer',
                        background: index === breadcrumbs.length - 1 ? 'rgba(59,130,246,0.08)' : 'transparent',
                        color: index === breadcrumbs.length - 1 ? '#2563eb' : '#94a3b8',
                        fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
                        fontSize: '13px',
                        transition: 'all 0.15s ease',
                        pointerEvents: index === breadcrumbs.length - 1 ? 'none' : 'auto',
                      }}
                    >
                      {index === 0 && <Home style={{ width: '14px', height: '14px' }} />}
                      {crumb.name}
                    </button>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Quick action buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setUploadDialogOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 14px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  color: '#fff', border: 'none', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(99,102,241,0.3)'; }}
              >
                <UploadCloud style={{ width: '15px', height: '15px' }} />
                Upload File
              </button>
              <button
                onClick={() => setCreateFolderDialogOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 14px', borderRadius: '10px',
                  background: '#fff', color: '#475569',
                  border: '1px solid #e2e8f0', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 500,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#fff'; }}
              >
                <FolderPlus style={{ width: '15px', height: '15px' }} />
                Folder Baru
              </button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>

            {/* Page Header */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
                }}>
                  <Sparkles style={{ width: '18px', height: '18px', color: '#fff' }} />
                </div>
                <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  {breadcrumbs.length === 1 ? 'My Drive' : breadcrumbs[breadcrumbs.length - 1].name}
                </h1>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0, paddingLeft: '46px' }}>
                {folders.length} folder · {files.length} file
              </p>
            </div>

            {/* Quick Actions Grid */}
            {breadcrumbs.length === 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '28px' }}>
                {/* Upload File Card */}
                <button
                  onClick={() => setUploadDialogOpen(true)}
                  style={{
                    padding: '20px', borderRadius: '16px',
                    background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                    border: '1.5px solid #bfdbfe',
                    cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex', flexDirection: 'column', gap: '8px',
                    boxShadow: '0 1px 4px rgba(59,130,246,0.06)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(59,130,246,0.18)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(59,130,246,0.06)'; }}
                >
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                  }}>
                    <UploadCloud style={{ width: '20px', height: '20px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af' }}>Upload File</div>
                    <div style={{ fontSize: '12px', color: '#60a5fa', marginTop: '2px' }}>Unggah file baru</div>
                  </div>
                </button>

                {/* Upload Folder Card */}
                <button
                  onClick={() => setFolderUploadDialogOpen(true)}
                  style={{
                    padding: '20px', borderRadius: '16px',
                    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                    border: '1.5px solid #bbf7d0',
                    cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex', flexDirection: 'column', gap: '8px',
                    boxShadow: '0 1px 4px rgba(16,185,129,0.06)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,185,129,0.18)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(16,185,129,0.06)'; }}
                >
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                  }}>
                    <FolderPlus style={{ width: '20px', height: '20px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#065f46' }}>Upload Folder</div>
                    <div style={{ fontSize: '12px', color: '#34d399', marginTop: '2px' }}>Unggah seluruh folder</div>
                  </div>
                </button>

                {/* Create Folder Card */}
                <button
                  onClick={() => setCreateFolderDialogOpen(true)}
                  style={{
                    padding: '20px', borderRadius: '16px',
                    background: 'linear-gradient(135deg, #fef9f0, #fef3c7)',
                    border: '1.5px solid #fde68a',
                    cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex', flexDirection: 'column', gap: '8px',
                    boxShadow: '0 1px 4px rgba(245,158,11,0.06)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(245,158,11,0.18)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(245,158,11,0.06)'; }}
                >
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
                  }}>
                    <FolderOpen style={{ width: '20px', height: '20px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#78350f' }}>Buat Folder Baru</div>
                    <div style={{ fontSize: '12px', color: '#fbbf24', marginTop: '2px' }}>Buat direktori baru</div>
                  </div>
                </button>
              </div>
            )}

            {/* Back Button */}
            {breadcrumbs.length > 1 && (
              <div style={{ marginBottom: '16px' }}>
                <Button variant="outline" onClick={handleBackButton} size="sm" style={{ gap: '8px', borderRadius: '8px' }}>
                  <ArrowLeft style={{ width: '14px', height: '14px' }} />
                  Kembali
                </Button>
              </div>
            )}

            {/* File List - in a smooth card */}
            <div style={{
              background: '#fff',
              borderRadius: '20px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}>
              <FileList
                files={files}
                folders={folders}
                onFileDelete={handleFileDelete}
                onFolderDelete={handleFolderDelete}
                onFileDownload={handleFileDownload}
                onFolderDownload={handleFolderDownload}
                onFolderOpen={handleFolderOpen}
                onPdfView={handlePdfView}
                onImageView={handleImageView}
                onFileRename={handleFileRename}
                onFolderRename={handleFolderRename}
                onFileMove={handleFileMove}
                currentFolderId={currentFolder}
                defaultViewMode={defaultViewMode}
              />
            </div>
          </div>
        </main>

        {/* Dialogs */}
        <FileUpload
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onUploadSuccess={loadFiles}
          folderId={currentFolder}
          droppedFiles={droppedFiles}
        />
        <FolderUpload
          open={folderUploadDialogOpen}
          onClose={() => setFolderUploadDialogOpen(false)}
          onUploadSuccess={loadFiles}
          parentId={currentFolder}
        />
        <CreateFolder
          open={createFolderDialogOpen}
          onClose={() => setCreateFolderDialogOpen(false)}
          onCreateSuccess={loadFiles}
          parentId={currentFolder}
        />
        <PdfViewer
          open={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
          file={currentPdfFile}
        />
        <ImageViewer
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          file={currentImageFile}
        />

        <FloatingActionMenu
          onUploadFile={() => setUploadDialogOpen(true)}
          onCreateFolder={() => setCreateFolderDialogOpen(true)}
          onUploadFolder={() => setFolderUploadDialogOpen(true)}
          currentFolder={currentFolder}
          onBack={handleBackButton}
          onHome={() => handleFolderClick(null)}
        />
      </div>
    </SidebarLayout>
  );
};

export default Dashboard;